import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Heart, CheckCircle2, BookOpen, Shuffle } from 'lucide-react';
import { QURAN_VERSES, getVerseById } from '../../data/quranVerses';
import { CrescentStarIcon, IslamicDivider } from '../common/IslamicIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const LIKED_KEY = 'liked_verses_local';
const RECITED_KEY = 'recited_verse_ids';
const NASKH = "'Amiri', 'Noto Naskh Arabic', serif";

const getAuthHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { 'Authorization': `Bearer ${t}` } : {};
};

/**
 * SavedVersesReader — shows all saved/liked verses.
 * Each verse can be marked as "Recited" which plants a flower in the Jannah Garden.
 */
const SavedVersesReader = ({ onRecited }) => {
  const { t } = useTranslation();
  const { isDarkMode, language, isRTL } = useTheme();
  const { isAuthenticated } = useAuth();
  const [likedIds, setLikedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LIKED_KEY) || '[]'); }
    catch { return []; }
  });
  const [recitedIds, setRecitedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(RECITED_KEY) || '[]')); }
    catch { return new Set(); }
  });
  const [expandedId, setExpandedId] = useState(null);

  // Sync liked IDs from backend
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API_URL}/api/verses/liked`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.liked_ids) setLikedIds(d.liked_ids); })
      .catch(() => {});
  }, [isAuthenticated]);

  const markRecited = (verseId) => {
    const newSet = new Set(recitedIds);
    newSet.add(verseId);
    setRecitedIds(newSet);
    localStorage.setItem(RECITED_KEY, JSON.stringify([...newSet]));
    onRecited?.(newSet.size);
  };

  const unmarkRecited = (verseId) => {
    const newSet = new Set(recitedIds);
    newSet.delete(verseId);
    setRecitedIds(newSet);
    localStorage.setItem(RECITED_KEY, JSON.stringify([...newSet]));
    onRecited?.(newSet.size);
  };

  const unlikeVerse = async (verseId) => {
    const newIds = likedIds.filter(id => id !== verseId);
    setLikedIds(newIds);
    localStorage.setItem(LIKED_KEY, JSON.stringify(newIds));
    if (!isAuthenticated) return;
    try {
      await fetch(`${API_URL}/api/verses/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ verse_id: verseId, liked: false }),
      });
    } catch (e) { console.error(e); }
  };

  const savedVerses = likedIds.map(id => getVerseById(id)).filter(Boolean);

  if (savedVerses.length === 0) {
    return (
      <div className={`rounded-2xl p-8 text-center ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}>
        <Heart className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`} />
        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('verse.noSaved')}
        </p>
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
          Tap the ♡ heart on any verse to save it here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Stats bar */}
      <div className={`flex items-center justify-between px-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {savedVerses.length} saved · {recitedIds.size} recited
        </p>
        <div className={`flex items-center gap-1 ${isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'}`}>
          <CrescentStarIcon size={12} color="currentColor" />
          <span className="text-[10px] font-bold uppercase tracking-wide">
            {t('jannah.gardenName', 'Jannah Garden')}
          </span>
        </div>
      </div>

      {savedVerses.map(verse => {
        const isRecited = recitedIds.has(verse.id);
        const isExpanded = expandedId === verse.id;
        const translation = verse.translations[language] || verse.translations.en;

        return (
          <div
            key={verse.id}
            data-testid={`saved-verse-${verse.id}`}
            className={`rounded-2xl overflow-hidden transition-all ${
              isRecited
                ? isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'
                : isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'
            }`}
          >
            {/* Header row */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : verse.id)}
              className={`w-full flex items-start justify-between p-4 text-left ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            >
              <div className="flex-1 min-w-0 pr-3">
                {/* Arabic text (preview) */}
                <p
                  className={`font-semibold leading-loose mb-1 ${
                    isRecited ? isDarkMode ? 'text-emerald-300' : 'text-emerald-800'
                    : isDarkMode ? 'text-amber-100' : 'text-[var(--p-hex)]'
                  }`}
                  style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1.1rem', lineHeight: '2' }}
                >
                  {verse.arabic.length > 80 && !isExpanded
                    ? verse.arabic.slice(0, 80) + '...'
                    : verse.arabic}
                </p>
                {/* Reference */}
                <span className={`text-[10px] font-semibold ${
                  isRecited ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                  : isDarkMode ? 'text-amber-400/70' : 'text-[var(--p-hex)]/60'
                }`}>
                  ﴾ {verse.reference} ﴿
                </span>
              </div>
              {/* Status icon */}
              <div className="flex-shrink-0">
                {isRecited
                  ? <CheckCircle2 className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  : <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                }
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="px-4 pb-4 animate-fade-up">
                <IslamicDivider isDarkMode={isDarkMode} className="mb-3 opacity-40" />

                {/* Full Arabic text */}
                {verse.arabic.length > 80 && (
                  <p
                    className={`text-center font-semibold leading-loose mb-3 ${isDarkMode ? 'text-amber-100' : 'text-[var(--p-hex)]'}`}
                    style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1.15rem', lineHeight: '2.2' }}
                  >
                    {verse.arabic}
                  </p>
                )}

                {/* Translation */}
                {language !== 'ar' && (
                  <p className={`text-sm leading-relaxed mb-4 text-center italic ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {translation}
                  </p>
                )}

                {/* Actions */}
                <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {isRecited ? (
                    <button
                      onClick={() => unmarkRecited(verse.id)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${
                        isDarkMode ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      }`}
                    >
                      ✓ {t('jannah.recited', 'Recited')}
                    </button>
                  ) : (
                    <button
                      data-testid={`recite-btn-${verse.id}`}
                      onClick={() => markRecited(verse.id)}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-[#0f172a] transition-colors"
                    >
                      🌱 {t('jannah.markRecited', 'I recited this verse')}
                    </button>
                  )}
                  <button
                    onClick={() => unlikeVerse(verse.id)}
                    className={`px-3 py-2.5 rounded-xl text-xs transition-colors ${
                      isDarkMode ? 'bg-white/5 text-gray-400 hover:text-red-400 hover:bg-red-500/10' : 'bg-gray-50 text-gray-400 hover:text-red-400 hover:bg-red-50'
                    }`}
                  >
                    <Heart className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export { SavedVersesReader };
export default SavedVersesReader;
